import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { cn } from "@replohq/lattice/utils/cn";
import {
  authButtonClassName,
  authFieldClassName,
} from "@v2/features/account/authFieldStyles";
import { AuthMethodDivider } from "@v2/features/account/AuthMethodDivider";
import { Screen } from "@v2/features/account/common";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { usePostAuthRedirect } from "@v2/features/account/usePostAuthRedirect";
import { useTheme } from "@v2/hooks/useTheme";
import { authRouter } from "@v2/trpc/routes/auth";
import { useForm, useWatch } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-utils/dom/misc";
import { isEmpty } from "replo-utils/lib/misc";
import { handleFormEnter } from "replo-utils/react/handle-form-enter";
import { getChatSignInTitle } from "schemas/actionLinkRoutes";
import * as z from "zod";

import { getMarketingWorkflow } from "./marketingWorkflow";

const emailSchema = z.email();

type LoginFormValues = {
  email: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const { effectiveMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const redirectAfterAuth = usePostAuthRedirect();

  const {
    mutate: loginApi,
    isPending: isLoadingLogin,
    error: loginError,
  } = authRouter.login.useMutation({
    meta: { reploIgnoreDefaultErrorHandling: true },
    onSuccess: ({ token }) => void redirectAfterAuth({ token }),
  });

  const { mutate: loginWithGoogleApi, error: googleLoginError } =
    authRouter.loginWithGoogle.useMutation({
      meta: { reploIgnoreDefaultErrorHandling: true },
      onSuccess: ({ token }) => void redirectAfterAuth({ token }),
    });

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : (searchParams.get("email") ?? ""),
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });

  const handleGoogleCredentials = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { credential, clientId } = credentialResponse;
    if (credential && clientId) {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      loginWithGoogleApi({
        credential,
        clientId,
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    }
  };

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    const pendingTemplateId = sessionStorage.getItem(
      "replo_pending_template_id",
    );
    loginApi({
      email,
      rawPassword: password,
      ...(pendingTemplateId ? { pendingTemplateId } : {}),
    });
  };

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;
  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const isWorkflowAuth = getMarketingWorkflow(searchParams) !== null;
  let submitLabel = "Sign in";
  if (isWorkflowAuth) {
    submitLabel = "Sign in and continue";
  }
  const loginErrorMessage = loginError?.message ?? googleLoginError?.message;
  let loginHeading: React.ReactNode = (
    <h1 className="font-editorial text-3xl font-bold text-default">
      Welcome to <span className="font-light italic">Replo</span>
    </h1>
  );
  const chatSignInTitle = getChatSignInTitle(searchParams.get("next"));
  if (isWorkflowAuth) {
    loginHeading = null;
  } else if (chatSignInTitle) {
    loginHeading = (
      <h1 className="font-editorial text-3xl font-bold text-default">
        {chatSignInTitle}
      </h1>
    );
  }
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      onKeyDown={handleFormEnter}
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div
        className={cn(
          "flex flex-col justify-center gap-2",
          isWorkflowAuth && "hidden",
          "mb-8",
          "items-center text-center",
        )}
      >
        {loginHeading}
      </div>
      {(loginErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !loginErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            loginErrorMessage ?? "Create an account or sign in to continue."
          }
        />
      )}
      <div className="flex flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            data-enter-navigation=""
            enterKeyHint="next"
            UNSAFE_className={authFieldClassName}
            invalid={Boolean(emailError)}
            autoFocus
            autoComplete="email"
            placeholder="you@company.com"
            {...register("email", {
              required: "Enter a valid email address.",
              validate: (value) => {
                const parsedEmail = emailSchema.safeParse(value);
                return parsedEmail.success || "Enter a valid email address.";
              },
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("email"),
            })}
            type="email"
            size="lg"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          id="password"
          labelAction={
            <Link
              to={
                watchAllFields.email
                  ? `${Screen.INIT_RESET_PASSWORD}?email=${encodeURIComponent(
                      watchAllFields.email,
                    )}`
                  : Screen.INIT_RESET_PASSWORD
              }
              className="text-sm font-medium text-default hover:underline"
            >
              Forgot password?
            </Link>
          }
          size="base"
          error={passwordError}
        >
          <Input
            id="password"
            data-enter-navigation=""
            enterKeyHint="go"
            UNSAFE_className={authFieldClassName}
            autoComplete="current-password"
            placeholder="••••••••••••"
            invalid={Boolean(passwordError)}
            {...register("password", {
              required: "Enter your password.",
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("password"),
            })}
            type="password"
            size="lg"
          />
        </LabeledControl>
      </div>
      <div className="mt-6 flex flex-col gap-y-4 overflow-hidden">
        <Button
          className="w-full"
          UNSAFE_className={authButtonClassName}
          variant="primary"
          size="lg"
          type="submit"
          loading={isLoadingLogin}
        >
          {submitLabel}
        </Button>
        <AuthMethodDivider />
        <GoogleAuth
          handleGoogleCredentials={handleGoogleCredentials}
          effectiveMode={effectiveMode}
        />
      </div>
    </form>
  );
};

export default LoginForm;
